import type { CellClassParams, RowClassParams } from "~/components/grid/AgGrid";

import logger from "~/helpers/logger";

interface RgbaColor {
	a: number;
	b: number;
	g: number;
	r: number;
}

function getGradientColor(
	value: number,
	minValue: number,
	maxValue: number,
	startColor: string,
	endColor: string
): string {
	// Helper function to convert HEX8 to RGBA

	function hex8ToRgba(hex: string): RgbaColor {
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		const a = parseInt(hex.slice(7, 9), 16) / 255;
		return { a, b, g, r };
	}

	// Helper function to convert RGBA back to HEX8
	function rgbaToHex8({ a, b, g, r }: RgbaColor): string {
		const toHex = (n: number): string => {
			const hex = Math.round(n).toString(16);
			return hex.length === 1 ? "0" + hex : hex;
		};
		const alphaHex = toHex(a * 255);
		return `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`;
	}

	// Input validation
	if (
		typeof value !== "number" ||
		isNaN(value) ||
		typeof minValue !== "number" ||
		isNaN(minValue) ||
		typeof maxValue !== "number" ||
		isNaN(maxValue) ||
		!/^#[0-9A-Fa-f]{8}$/.test(startColor) ||
		!/^#[0-9A-Fa-f]{8}$/.test(endColor)
	) {
		logger.warn("Invalid input parameters");
		logger.warn("getGradientColor called with:", { endColor, maxValue, minValue, startColor, value });

		return "#99999999";
	}

	// Convert input colors to RGBA
	const start: RgbaColor = hex8ToRgba(startColor);
	const end: RgbaColor = hex8ToRgba(endColor);

	// Calculate the ratio (0 to 1) of where the value sits between min and max
	const ratio: number = maxValue === minValue ? 0 : (value - minValue) / (maxValue - minValue);
	// Clamp ratio between 0 and 1
	const clampedRatio: number = Math.max(0, Math.min(1, ratio));

	// Interpolate each component
	const r: number = start.r + (end.r - start.r) * clampedRatio;
	const g: number = start.g + (end.g - start.g) * clampedRatio;
	const b: number = start.b + (end.b - start.b) * clampedRatio;
	const a: number = start.a + (end.a - start.a) * clampedRatio;

	// Convert back to HEX8
	return rgbaToHex8({ a, b, g, r });
}

export const checkCondition = (params: CellClassParams | RowClassParams, rule: GridFormattingRule) => {
	const results: boolean[] = [];
	const ruleStyle = { ...rule.style };

	const isGrouped = params.api.getRowGroupColumns().length > 0 || params.api.getPivotColumns().length > 0;

	rule.conditions.forEach((condition) => {
		const column = params.api.getColumn(condition.column as string);
		const currentCellValue = params.api.getCellValue({
			colKey: column || "",
			rowNode: params.node,
			useFormatter: true
		});
		const currentCellValueNumeric = parseFloat(String(currentCellValue).replace(/[^0-9.-]+/g, ""));

		if (rule.ruleType === "unique" && !isGrouped) {
			let matches = 0;
			//const currentCellValue = params.data[condition.column as string];
			params.api.forEachNode((rowNode) => {
				const cellValue = rowNode.data[condition.column as string];
				if (typeof cellValue === "string") {
					if (cellValue === currentCellValue) {
						matches++;
					}
				} else {
					if (JSON.stringify(cellValue) === JSON.stringify(currentCellValue)) {
						matches++;
					}
				}
			});
			if (condition.type?.unique === "unique") {
				results.push(matches === 1); // Unique if exactly one match
			} else {
				results.push(matches > 1); // Duplicate if more than one match
			}
		} else if (rule.ruleType === "ranking" && !isGrouped) {
			try {
				if (isNaN(currentCellValueNumeric)) {
					logger.warn("Invalid numeric value detected:", currentCellValueNumeric);
					results.push(false);
					return;
				}
				// Array to store all values with their row data
				const allValues: { rowData: unknown; value: unknown }[] = [];
				const findTop = condition.type?.ranking === "top";
				// Collect all values from the grid
				params.api.forEachNode((rowNode) => {
					const cellValue = rowNode.data[condition.column as string];
					allValues.push({
						rowData: rowNode.data,
						value: typeof cellValue === "string" ? parseFloat(cellValue.replace(/[^0-9.-]+/g, "")) : cellValue
					});
				});

				// Sort values based on findTop parameter
				allValues.sort((a, b) => {
					const valA = Number(a.value);
					const valB = Number(b.value);

					if (findTop) {
						// For top values (descending order)
						return valB > valA ? 1 : valB < valA ? -1 : 0;
					} else {
						// For bottom values (ascending order)
						return valA > valB ? 1 : valA < valB ? -1 : 0;
					}
				});

				// Return top or bottom X values
				const xResults = allValues.slice(0, Number(condition.value));
				const cellValue = currentCellValueNumeric;
				const isCurrentValueExtreme = xResults.some((item) => Number(item.value) === cellValue);
				if (rule.styleType === "double") {
					const startColor = rule.style.backgroundColor;
					const endColor = rule.style2?.backgroundColor || "#00000000";
					const gradientColor = getGradientColor(
						cellValue,
						Number(xResults[xResults.length - 1].value),
						Number(xResults[0].value),
						startColor,
						endColor
					);
					const startTextColor = rule.style.color;
					const endTextColor = rule.style2?.color || "#00000000";
					const gradientTextColor = getGradientColor(
						cellValue,
						Number(xResults[xResults.length - 1].value),
						Number(xResults[0].value),
						startTextColor,
						endTextColor
					);
					ruleStyle.color = gradientTextColor;

					ruleStyle.backgroundColor = gradientColor;
				}
				results.push(isCurrentValueExtreme);
			} catch (error) {
				logger.error("Error in ranking condition check:", error);
				results.push(false);
			}
		} else if (rule.ruleType === "average" && !isGrouped) {
			const numericValues = [];
			let sum = 0;
			let max = 0;
			let min = 0;

			if (isNaN(currentCellValueNumeric)) {
				results.push(false);
				return;
			}
			// Collect all values and calculate sum (only for numeric values)
			params.api.forEachNode((rowNode) => {
				const cellValue = rowNode.data[condition.column as string];
				// Convert to number if possible
				const numericValue = Number(cellValue);
				if (!isNaN(numericValue)) {
					numericValues.push({
						rowData: rowNode.data,
						value: numericValue
					});
					sum += numericValue;
					if (numericValue > max) {
						max = numericValue;
					}
					if (min === 0 || numericValue < min) {
						min = numericValue;
					}
				}
			});
			const average = numericValues.length > 0 ? sum / numericValues.length : 0;

			if (rule.styleType === "double") {
				const startColor = rule.style.backgroundColor;
				const endColor = rule.style2?.backgroundColor || "#00000000";
				const startTextColor = rule.style.color;
				const endTextColor = rule.style2?.color || "#00000000";
				const gradientMin = condition.type?.average === "above" ? average : min;
				const gradientMax = condition.type?.average === "above" ? max : average;
				const gradientColor = getGradientColor(currentCellValueNumeric, gradientMin, gradientMax, startColor, endColor);
				const gradientTextColor = getGradientColor(
					currentCellValueNumeric,
					gradientMin,
					gradientMax,
					startTextColor,
					endTextColor
				);
				ruleStyle.color = gradientTextColor;
				ruleStyle.backgroundColor = gradientColor;
			}

			// Calculate average

			// Check if current cell value is above or below average
			if (condition.type?.average === "above") {
				results.push(currentCellValueNumeric > average);
			}
			if (condition.type?.average === "below") {
				results.push(currentCellValueNumeric < average);
			}
		} else if (rule.ruleType === "value") {
			if (condition.dataType === "string") {
				const cellValue = String(currentCellValue).toLowerCase();
				const stringRuleValue = String(condition.value).toLowerCase();
				switch (condition.type?.string) {
					case "contains":
						results.push(cellValue.includes(stringRuleValue!));
						break;
					case "notContains":
						results.push(!cellValue.includes(stringRuleValue!));
						break;
					case "equalTo":
						results.push(cellValue === stringRuleValue!);
						break;
					case "notEqualTo":
						results.push(cellValue !== stringRuleValue!);
						break;
					case "startsWith":
						results.push(cellValue.startsWith(stringRuleValue!));
						break;
					case "endsWith":
						results.push(cellValue.endsWith(stringRuleValue!));
						break;
					default:
						results.push(false);
				}
			} else {
				try {
					const numRuleValue =
						typeof condition.value !== "number" ? parseFloat(String(condition.value)) : condition.value;
					const numRuleValue2 =
						typeof condition.value2 !== "number" ? parseFloat(String(condition.value2)) : condition.value2;

					if (
						isNaN(currentCellValueNumeric) ||
						numRuleValue === undefined ||
						(condition.type?.numeric === "between" && numRuleValue2 === undefined)
					) {
						logger.warn("Invalid numeric value detected:", { currentCellValueNumeric, numRuleValue, numRuleValue2 });
						results.push(false);
						return;
					}

					switch (condition.type?.numeric) {
						case "greaterThan":
							results.push(currentCellValueNumeric > numRuleValue!);
							break;
						case "lessThan":
							results.push(currentCellValueNumeric < numRuleValue!);
							break;
						case "equalTo":
							results.push(currentCellValueNumeric === numRuleValue!);
							break;
						case "between":
							//check for a match then check for a gradient
							if (rule.styleType === "double") {
								const startColor = rule.style.backgroundColor;
								const endColor = rule.style2?.backgroundColor || "#00000000";
								const gradientColor = getGradientColor(
									currentCellValueNumeric,
									numRuleValue!,
									numRuleValue2!,
									startColor,
									endColor
								);
								const startTextColor = rule.style.color;
								const endTextColor = rule.style2?.color || "#00000000";
								const gradientTextColor = getGradientColor(
									currentCellValueNumeric,
									numRuleValue!,
									numRuleValue2!,
									startTextColor,
									endTextColor
								);
								ruleStyle.color = gradientTextColor;

								ruleStyle.backgroundColor = gradientColor;
							}
							results.push(
								numRuleValue2 !== null &&
									currentCellValueNumeric >= numRuleValue! &&
									currentCellValueNumeric <= numRuleValue2!
							);
							break;
						default:
							results.push(false);
					}
				} catch (error) {
					logger.error("Error in numeric condition check:", error);
					results.push(false);
				}
			}
		}
	});
	if (results.length === 0) {
		return false; // No conditions to check
	}
	const pass = rule.andor === "and" ? results.every(Boolean) : results.some(Boolean);
	return pass ? ruleStyle : false;
};
export default checkCondition;
